import type { JSONSchema7 } from "json-schema";
import fallbackSchema from "./sdk-schema.json";

/** SDK annotations and the object-schema subset supported by the settings form. */
export interface JsonSchema extends JSONSchema7 {
  private?: string | boolean;
  deprecated?: string | boolean;
  properties?: Record<string, JsonSchema>;
  anyOf?: JsonSchema[];
  items?: JsonSchema;
}

let definitions: Record<string, JsonSchema>;
export let sdkSectionSchemas: Record<string, JsonSchema>;

/** Versioned scripts have sibling schemas; legacy aliases use the origin's latest schema. */
export const getSdkSchemaUrl = (sdkSource: string): string | undefined => {
  if (sdkSource.toLowerCase() === "npm") return undefined;
  const url = new URL(sdkSource, window.location.href);
  const filename = url.pathname.split("/").pop() ?? "";
  if (/^glean-web-sdk-.+\.min\.js$/.test(filename)) {
    url.pathname = url.pathname.replace(/\.min\.js$/, ".schema.json");
  } else {
    url.pathname = "/glean-web-sdk-latest.schema.json";
  }
  return url.href;
};

/** Returns true when the bundled fallback is used instead of a matching remote schema. */
export const loadSdkSchema = async (sdkSource: string, signal?: AbortSignal): Promise<boolean> => {
  let schema = fallbackSchema;
  let usedFallback = true;
  try {
    const url = getSdkSchemaUrl(sdkSource);
    if (url) {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Schema request failed: HTTP ${response.status}`);
      const fetchedSchema = await response.json();
      for (const type of ["Options", "SearchOptions", "SearchBoxOptions", "TabbedSearchOptions", "ChatOptions"]) {
        if (!fetchedSchema?.definitions?.[type]?.properties) {
          throw new Error(`SDK schema is missing ${type} properties`);
        }
      }
      schema = fetchedSchema;
      usedFallback = false;
    }
  } catch (error) {
    if (signal?.aborted) throw error;
    console.warn(`Unable to load schema for ${sdkSource}; using the bundled fallback.`, error);
  }
  // Ignore stale requests when the SDK source changes or the component unmounts.
  if (signal?.aborted) throw new Error("Schema request aborted");
  setSchema(schema.definitions as Record<string, JsonSchema>);
  return usedFallback;
};

const omitProperties = (schema: JsonSchema, omitFrom: JsonSchema): JsonSchema => ({
  ...schema,
  properties: Object.fromEntries(
    Object.entries(schema.properties ?? {}).filter(([key]) => !(key in (omitFrom.properties ?? {})))
  ),
});

const setSchema = (nextDefinitions: Record<string, JsonSchema>) => {
  definitions = nextDefinitions;
  sdkSectionSchemas = {
    Options: definitions.Options,
    "Search Options": omitProperties(definitions.SearchOptions, definitions.Options),
    "Search Box": omitProperties(definitions.SearchBoxOptions, definitions.SearchOptions),
    "Search Results": omitProperties(definitions.TabbedSearchOptions, definitions.SearchOptions),
    Chat: omitProperties(definitions.ChatOptions, definitions.Options),
  };
};

setSchema(fallbackSchema.definitions as Record<string, JsonSchema>);

/** Follows `$ref`s, keeping the description and annotations of the referencing property. */
export const resolveSchema = (schema: JsonSchema): JsonSchema => {
  if (!schema.$ref) return schema;
  const { $ref, ...annotations } = schema;
  const target = definitions[$ref.replace("#/definitions/", "")];
  return { ...resolveSchema(target), ...annotations };
};

export const isPrivate = (schema: JsonSchema) => schema.private !== undefined && schema.private !== false;

/** The allowed literal values of a schema, if it's a union of string/number literals. */
export const enumValues = (schema: JsonSchema): unknown[] | undefined => {
  const resolved = resolveSchema(schema);
  if (resolved.enum) return resolved.enum;
  if (resolved.const !== undefined) return [resolved.const];
  if (!resolved.anyOf) return undefined;
  const values: unknown[] = [];
  for (const option of resolved.anyOf) {
    const optionValues = enumValues(option);
    if (!optionValues) return undefined;
    values.push(...optionValues);
  }
  return values;
};

/** Every option the schema knows about, nested objects included, with undefined values. */
export const schemaSkeleton = (schema: JsonSchema, showPrivate: boolean): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(resolveSchema(schema).properties ?? {})
      .filter(([, property]) => showPrivate || !isPrivate(property))
      .map(([key, property]) => {
        const resolved = resolveSchema(property);
        return [key, resolved.type === "object" && resolved.properties ? schemaSkeleton(resolved, showPrivate) : undefined];
      })
  );

/** Paths of set values that the schema doesn't define, e.g. options removed from the SDK. */
export const unknownOptionPaths = (value: unknown, schema: JsonSchema, path: string[] = []): string[][] => {
  const resolved = resolveSchema(schema);
  if (!resolved.properties || value === null || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value).flatMap(([key, nestedValue]) => {
    if (nestedValue === undefined) return [];
    const property = resolved.properties?.[key];
    return property ? unknownOptionPaths(nestedValue, property, [...path, key]) : [[...path, key]];
  });
};

/** Playground-only sections, which aren't SDK options. */
export const playgroundSectionSchemas = {
  "SDK Options": {
    type: "object",
    properties: {
      source: { type: "string", description: "URL of the Web SDK script to load, or `npm` to use the @gleanwork/web-sdk package." },
      integrity: { type: "string", description: "Subresource integrity hash for the SDK script." },
    },
  },
  "Auth Options": {
    type: "object",
    properties: {
      type: {
        type: "string",
        enum: ["default", "sso", "token", "server-side", "anonymous"],
        description: "How the playground authenticates the embedded widget.",
      },
      actAs: { type: "string", description: "Email of the user to act as for server-side auth." },
      apiKey: { type: "string", description: "API key used by the playground server to mint auth tokens." },
      authToken: {
        type: "object",
        description: "A pre-minted auth token, for `token` auth.",
        properties: { token: { type: "string" }, expirationTime: { type: "number" } },
      },
    },
  },
} satisfies Record<string, JsonSchema>;
